/**
 * Format a money amount stored as minor units (cents) to a localized string.
 *
 * @example formatMoney(2900, 'CAD') → 'CA$29.00'
 * @example formatMoney(2900n, 'CAD', { locale: 'en-CA' }) → 'CA$29.00'
 * @example formatMoney(0, 'CAD') → 'CA$0.00'
 * @example formatMoney(null) → '—'
 */
export function formatMoney(
  minor: number | bigint | null | undefined,
  currency: string | null | undefined = 'CAD',
  options: {
    locale?: string;
    signDisplay?: 'auto' | 'always' | 'never';
    /**
     * `'code'` prints `CAD 10.00` instead of `$10.00`.
     *
     * Use it wherever one column can hold more than one currency. Under
     * `en-CA` a Canadian dollar renders as a bare `$`, so a vendor list of
     * `BDT 112.50 / $10.00 / €500.00` gives no way to tell a Canadian dollar
     * from a US one — in a table that feeds burn, which is the one place it
     * matters.
     */
    currencyDisplay?: 'symbol' | 'code';
    /**
     * Whole units, for summary cards: `৳30,801` rather than `BDT 30,801.00`.
     * Statements and invoices keep the exact figure (#918).
     */
    whole?: boolean;
  } = {},
): string {
  if (minor === null || minor === undefined) return '—';
  const amount = typeof minor === 'bigint' ? Number(minor) : minor;
  const cur = currency ?? 'CAD';
  const minorPerMajor = MINOR_UNITS[cur.toUpperCase()] ?? 100;
  return new Intl.NumberFormat(options.locale ?? 'en-CA', {
    style: 'currency',
    currency: cur.toUpperCase(),
    signDisplay: options.signDisplay ?? 'auto',
    currencyDisplay: options.currencyDisplay ?? 'symbol',
    ...(options.whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
  }).format(amount / minorPerMajor);
}

/**
 * A point in time the way a messenger shows it (#918): `1:24 PM` today,
 * `Yesterday`, `Oct 9` this year, `Oct 9, 2025` before. Never seconds. Put the
 * exact time in a `title` with `formatExact` when someone may need it.
 */
export function formatStamp(
  date: Date | string,
  now: Date = new Date(),
  locale = 'en-CA',
  labels: { yesterday?: string } = {},
): string {
  const then = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(then.getTime())) return '—';
  const dayStart = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((dayStart(now) - dayStart(then)) / 86_400_000);
  if (days === 0) {
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(then);
  }
  if (days === 1) {
    return (
      labels.yesterday ??
      capitalise(new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-1, 'day'))
    );
  }
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(then.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(then);
}

/** The full date and time to the minute, for a tooltip: `Oct 9, 2026, 1:24 PM`. */
export function formatExact(date: Date | string, locale = 'en-CA'): string {
  const then = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(then.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(then);
}

/**
 * An exchange rate a person can read (#918): `1 CAD = 88.90 BDT · Sep 7`.
 * Two decimals, never six; the date says how old the rate is.
 */
export function formatRate(
  rate: number,
  from: string,
  to: string,
  asOf?: Date | string | null,
  locale = 'en-CA',
): string {
  const value = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate);
  const head = `1 ${from.toUpperCase()} = ${value} ${to.toUpperCase()}`;
  if (!asOf) return head;
  const when = typeof asOf === 'string' ? new Date(asOf) : asOf;
  if (Number.isNaN(when.getTime())) return head;
  return `${head} · ${new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(when)}`;
}

/**
 * A country as people think of it (#918): `🇸🇦 Saudi Arabia` for `SA`.
 * Unknown codes come back as given, so nothing is ever blank.
 */
export function countryLabel(
  code: string | null | undefined,
  locale = 'en-CA',
  options: { flag?: boolean } = {},
): string {
  if (!code) return '—';
  const upper = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return code;
  let name = upper;
  try {
    name = new Intl.DisplayNames([locale], { type: 'region' }).of(upper) ?? upper;
  } catch {
    // An engine without DisplayNames keeps the code.
  }
  if (options.flag === false) return name;
  const flag = String.fromCodePoint(...[...upper].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
  return `${flag} ${name}`;
}

function capitalise(s: string): string {
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}

/**
 * The inverse of `formatMoney`: what a person typed, as minor units.
 *
 * WHY THIS EXISTS. Until 2026-09-07 this module knew that JPY has no minor
 * digits and KWD has three - but only when DISPLAYING money. Every dialog that
 * SAVED money wrote `BigInt(Math.round(value * 100))` by hand, so the vendor
 * form (which lets you pick any currency) would store a typed 1000 JPY as
 * 100,000 minor units and render it back as the same 100,000: a clean
 * hundredfold error with nothing on screen to reveal it. One table, used in
 * both directions, is the only way that stays fixed.
 *
 * It also never multiplies by a float. `Math.round(x * 100)` is correct for
 * almost every two-decimal amount and silently is not for some - 4.475 * 100 is
 * 447.49999999999994, which rounds DOWN to 447. Reading the digits of the
 * string is exact for every input, and rounds half away from zero on anything
 * typed finer than the currency can hold.
 *
 * Returns `null` for anything that is not a number, so a caller can disable its
 * submit button rather than send NaN - `BigInt(NaN)` throws.
 *
 * @example parseMoneyToMinor('89.25', 'BDT') -> 8925n
 * @example parseMoneyToMinor('1000', 'JPY') -> 1000n
 * @example parseMoneyToMinor('-12.5', 'CAD') -> -1250n
 * @example parseMoneyToMinor('abc', 'CAD') -> null
 */
export function parseMoneyToMinor(
  text: string,
  currency: string | null | undefined = 'CAD',
  options: { decimals?: number } = {},
): bigint | null {
  const match = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(text.trim());
  if (!match) return null;
  const [, sign, wholeRaw = '', fracRaw = ''] = match;
  // "." and "" are not amounts; "5." and ".5" are.
  if (wholeRaw === '' && fracRaw === '') return null;

  const minorPerMajor = MINOR_UNITS[(currency ?? 'CAD').toUpperCase()] ?? 100;
  const digits = options.decimals ?? String(minorPerMajor).length - 1;
  const scale = 10n ** BigInt(digits);

  const kept = (fracRaw + '0'.repeat(digits)).slice(0, digits);
  const dropped = fracRaw.slice(digits);
  let value = BigInt(wholeRaw || '0') * scale + BigInt(kept || '0');
  // Half away from zero, decided on the first digit the currency cannot hold.
  if (dropped !== '' && Number(dropped[0]) >= 5) value += 1n;
  return sign === '-' ? -value : value;
}

/**
 * Format a basis-point markup as a percent string.
 * @example formatBps(30) → '0.30%'
 * @example formatBps(150) → '1.50%'
 */
export function formatBps(bps: number | bigint | null | undefined): string {
  if (bps === null || bps === undefined) return '—';
  const n = typeof bps === 'bigint' ? Number(bps) : bps;
  return `${(n / 100).toFixed(2)}%`;
}

/**
 * Format a percentage (already in percent units, e.g. 32.4).
 * @example formatPercent(32.4) → '32.4%'
 */
export function formatPercent(pct: number | null | undefined, fractionDigits = 1): string {
  if (pct === null || pct === undefined) return '—';
  return `${pct.toFixed(fractionDigits)}%`;
}

/**
 * Format a relative date string like "2 hours ago", "3 days ago".
 * Uses `Intl.RelativeTimeFormat`, so the words come from the browser in the
 * `locale` given (#714: admin passes the reader's language through
 * `useUiLabels().relativeTime`).
 */
export function formatRelativeTime(
  date: Date | string,
  now: Date = new Date(),
  locale = 'en',
): string {
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffSec = Math.round((then.getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 2_592_000) return rtf.format(Math.round(diffSec / 86_400), 'day');
  if (abs < 31_536_000) return rtf.format(Math.round(diffSec / 2_592_000), 'month');
  return rtf.format(Math.round(diffSec / 31_536_000), 'year');
}

/**
 * Currencies with non-100 minor units. Most are 100; this is the override table.
 * Source: ISO 4217.
 */
const MINOR_UNITS: Readonly<Record<string, number>> = {
  JPY: 1,
  KRW: 1,
  VND: 1,
  IDR: 1,
  CLP: 1,
  ISK: 1,
  KWD: 1000,
  BHD: 1000,
  OMR: 1000,
  JOD: 1000,
  TND: 1000,
};
